/**
 * PSP/CRE Tool v2.0 - Operational States Constants
 * 
 * Purpose: Define the three fundamental operational states that determine
 * a unit's battle casualty rate and DNBI rate with full calculator integration.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 21, 2024
 * Version: 2.3 - IMP-008: Add STRENGTH_COHESION_MODIFIERS + getCohesionModifier/getCohesionBand (March 10, 2026)
 *
 * Changes in v2.3 (March 10, 2026):
 * - IMP-008: Added `STRENGTH_COHESION_MODIFIERS` — 4-band per-capita casualty rate multiplier
 *   keyed to FM 101-5-1 Gumball Chart strength thresholds (GREEN/AMBER/RED/BLACK).
 *   Modifiers: FULL_EFFECTIVE ≥85% → 1.00×, REDUCED_CAPABILITY 70–84% → 1.08×,
 *   MISSION_DEGRADED 50–69% → 1.20×, COMBAT_INEFFECTIVE 30–49% → 1.40×.
 *   Source: US FM 101-5-1 Appendix C; Dupuy TNDM cohesion research.
 * - IMP-008: Added `getCohesionModifier(strengthPercent)` — returns modifier for band.
 * - IMP-008: Added `getCohesionBand(strengthPercent)` — returns band label for Tab 5 display.
 *
 * Changes in v2.2 (February 24, 2026):
 * - SPM-02: Added `STRENGTH_EFFECTIVENESS` export. Migrated from strengthProjectionModule.js.
 *   5 bands: FULL_EFFECTIVE (≥85%), REDUCED_CAPABILITY (≥70%), MISSION_DEGRADED (≥50%),
 *   COMBAT_INEFFECTIVE (≥30%), DESTROYED (<30%). Source: NATO AJP-3.3; FM 3-96.
 * - SPM-06: Added `PERSONNEL_CATEGORIES` export. Migrated from strengthProjectionModule.js.
 *   3 categories: COMBAT (priority 1.0), MEDICAL (1.2), SUPPORT (0.8).
 * Version: 2.1 - CRITICAL FIX: TRUE_RESERVE battle_casualty_multiplier
 * 
 * CHANGELOG v2.1 (January 31, 2026):
 * - CRITICAL FIX: TRUE_RESERVE battle_casualty_multiplier: 0.05 → 0.3
 *   - Was 5% (WAY too low for modern warfare)
 *   - Now 30% (reflects deep strike risk to reserve areas)
 *   - Based on Ukraine 2022-2024 data showing 8% observed casualties
 *   - Accounts for: Strategic strikes, long-range fires, training accidents
 *   - Examples: Makiivka barracks attack, missile strikes on rear areas
 *   - Reserve ≠ Safe in modern high-intensity conflict
 * 
 * References:
 * - NATO AMedP-1.5 Casualty Estimation
 * - FM 4-0 Sustainment
 * - Ukraine 2022-2024 combat data
 * - Dupuy Institute casualty research
 * 
 * Key Enhancements v2.0:
 * - All icons imported from icons.js (no hardcoding)
 * - Ukraine 2022-2024 validated rates
 * - Enhanced DNBI rates by state
 * - CSR (Combat Stress Reaction) integration
 * - Medical planning factors
 * 
 * The Three States:
 * 1. COMBAT_ENGAGED - Units on the FEBA, actively fighting (100% battle casualties)
 * 2. SUPPORT_ACTIVE - Units supporting operations but not in direct combat (20% battle casualties)
 * 3. TRUE_RESERVE - Units in rest/regeneration status (30% battle casualties)
 * 
 * Usage:
 * import { 
 *   OPERATIONAL_STATES,
 *   getBattleCasualtyMultiplier,
 *   getDNBIRate,
 *   getDefaultStateForUnitType
 * } from '../constants/operationalStates';
 */

import { ICONS, TACTICAL_ICONS } from '../utils/icons';
import { lookupByUnitType } from './unitTypeRegistry';

// ============================================================================
// OPERATIONAL STATES DEFINITIONS
// ============================================================================

export const OPERATIONAL_STATES = {
  COMBAT_ENGAGED: {
    value: 'COMBAT_ENGAGED',
    label: 'Combat Engaged',
    shortLabel: 'COMBAT',
    icon: TACTICAL_ICONS.ATTACK,
    
    description: 'Units on frontage, actively engaged in combat operations',
    detailed_description: 'Units positioned on or near the FEBA (Forward Edge of Battle Area), ' +
      'directly engaged with enemy forces. Subject to direct fire, artillery, and close combat. ' +
      'Highest casualty exposure.',
    
    // Position relative to FEBA
    position_range_km: { min: 0, max: 15 },
    typical_position_km: 5,
    
    // Casualty Modifiers
    battle_casualty_multiplier: 1.0,    // Base rate (100%)
    
    // DNBI Rates (per 1,000 per day)
    dnbi: {
      base_rate: 2.0,
      stress_factor: 1.5,        // Combat stress increases DNBI
      hygiene_factor: 1.3,       // Limited hygiene increases disease
      sleep_deprivation: 1.2,    // Fatigue increases accidents
      combined_rate: 4.7         // Calculated: 2.0 × 1.5 × 1.3 × 1.2 ≈ 4.7
    },
    
    // CSR (Combat Stress Reaction)
    csr: {
      rate_per_1000_day: 3.5,
      cumulative_risk_7_days: 0.15,    // 15% after 7 days combat
      cumulative_risk_30_days: 0.35,   // 35% after 30 days combat
      rtd_probability: 0.65            // 65% Return to Duty
    },
    
    // Ukraine 2022-2024 Data
    ukraine_data: {
      observed_multiplier: 1.0,
      notes: 'Baseline for engaged units',
      dnbi_observed: 4.5,
      csr_observed: 4.0,
      lessons: [
        'Rotation after 7 days critical for combat effectiveness',
        'CSR rates higher than historical NATO estimates',
        'Drone threat increases psychological stress',
        'Trench warfare conditions increase disease'
      ]
    },
    
    // Visual indicators
    color: 'error',
    severity: 'error',
    badge_color: '#f44336',              // Red
    
    // Unit type associations
    typical_unit_types: [
      'INFANTRY', 'MECHANIZED', 'ARMOR', 'AIRBORNE', 
      'MOUNTAIN', 'RECONNAISSANCE'
    ],
    
    // Planning factors
    planning: {
      medical_support_priority: 'HIGHEST',
      evacuation_priority: 'URGENT',
      resupply_frequency: 'DAILY',
      rest_requirement_days: 3,          // Should rotate after 3 days
      max_continuous_days: 7,            // Maximum before severe degradation
      recommended_rotation_days: 5       // Optimal rotation cycle
    },
    
    // Medical planning
    medical: {
      role1_distance_km: 5,
      role2_distance_km: 20,
      golden_hour_critical: true,
      medevac_priority: 'IMMEDIATE',
      blood_products_forward: true,
      surgical_teams_forward: true
    },
    
    notes: 'Full casualty rate applies. Golden hour critical. Maximum medical support required.',
    romanian_term: 'Angajat în Luptă'
  },
  
  SUPPORT_ACTIVE: {
    value: 'SUPPORT_ACTIVE',
    label: 'Support Active',
    shortLabel: 'SUPPORT',
    icon: ICONS.GENERAL.GEAR,
    
    description: 'Support units actively working but not in direct combat',
    detailed_description: 'Units providing combat support and combat service support. ' +
      'Located behind the FEBA but within range of indirect fire and rear area threats. ' +
      'Essential to sustaining combat operations.',
    
    position_range_km: { min: 15, max: 60 },
    typical_position_km: 30,
    
    battle_casualty_multiplier: 0.2,    // 20% of base rate
    
    dnbi: {
      base_rate: 1.0,
      stress_factor: 1.1,
      hygiene_factor: 1.1,
      sleep_deprivation: 1.1,
      combined_rate: 1.3
    },
    
    csr: {
      rate_per_1000_day: 1.0,
      cumulative_risk_7_days: 0.05,
      cumulative_risk_30_days: 0.12,
      rtd_probability: 0.80
    },
    
    ukraine_data: {
      observed_multiplier: 0.25,
      notes: 'Deep strikes increased support casualties above historical',
      dnbi_observed: 1.5,
      csr_observed: 1.2,
      lessons: [
        'Deep strikes (missiles, drones) hit support units',
        'Logistics convoys high-value drone targets',
        'Artillery support units subject to counter-battery',
        'Medical facilities deliberately targeted'
      ],
      deep_strike_factor: 1.25   // 25% increase due to deep strikes
    },
    
    color: 'warning',
    severity: 'warning',
    badge_color: '#ff9800',              // Orange
    
    typical_unit_types: [
      'ARTILLERY', 'ENGINEERS', 'SIGNAL', 'AIR_DEFENSE', 
      'MILITARY_POLICE', 'CBRN_DEFENSE', 'MILITARY_INTELLIGENCE'
    ],
    
    planning: {
      medical_support_priority: 'MEDIUM',
      evacuation_priority: 'PRIORITY',
      resupply_frequency: 'EVERY_2_DAYS',
      rest_requirement_days: 7,
      max_continuous_days: 14
    },
    
    medical: {
      role1_distance_km: 15,
      role2_distance_km: 30,
      golden_hour_critical: false,
      medevac_priority: 'PRIORITY',
      blood_products_forward: false,
      surgical_teams_forward: false
    },
    
    notes: 'Reduced battle casualties but still exposed to indirect fire and deep strikes.',
    romanian_term: 'Sprijin Activ'
  },
  
  TRUE_RESERVE: {
    value: 'TRUE_RESERVE',
    label: 'True Reserve',
    shortLabel: 'RESERVE',
    icon: TACTICAL_ICONS.RESERVE,
    
    description: 'Units in rest, regeneration, or strategic reserve',
    detailed_description: 'Units pulled from combat operations for rest, reconstitution, ' +
      'training, or held as strategic reserve. Minimal combat exposure but subject to ' +
      'long-range fires and unconventional threats.',
    
    position_range_km: { min: 60, max: 150 },
    typical_position_km: 80,
    
    battle_casualty_multiplier: 0.08,    // 8% of base rate (FIXED: was 0.05) same as Ukraine data
    
    dnbi: {
      base_rate: 0.5,
      stress_factor: 0.8,        // Reduced stress during rest
      hygiene_factor: 0.9,       // Better hygiene in rear
      sleep_deprivation: 0.9,    // Better sleep
      combined_rate: 0.3
    },
    
    csr: {
      rate_per_1000_day: 0.3,
      cumulative_risk_7_days: 0.02,
      cumulative_risk_30_days: 0.05,
      rtd_probability: 0.90,
      recovery_rate: 0.85        // 85% of existing CSR cases recover
    },
    
    ukraine_data: {
      observed_multiplier: 0.08,
      notes: 'Strategic missile/drone strikes reach reserve areas',
      dnbi_observed: 0.5,
      csr_observed: 0.4,
      lessons: [
        'Reserve areas not completely safe',
        'Barracks/training areas targeted',
        'Makiivka barracks attack killed 89+',
        'Rest is critical for combat effectiveness recovery'
      ]
    },
    
    color: 'default',
    severity: 'info',
    badge_color: '#9e9e9e',              // Grey
    
    typical_unit_types: [
      'LOGISTICS', 'MEDICAL', 'MAINTENANCE', 'TRANSPORT',
      'QUARTERMASTER', 'ORDNANCE', 'TRAINING', 'HQ'
    ],
    
    planning: {
      medical_support_priority: 'ROUTINE',
      evacuation_priority: 'ROUTINE',
      resupply_frequency: 'WEEKLY',
      rest_requirement_days: null,
      max_continuous_days: null
    },
    
    medical: {
      role1_distance_km: 30,
      role2_distance_km: 50,
      golden_hour_critical: false,
      medevac_priority: 'ROUTINE',
      blood_products_forward: false,
      surgical_teams_forward: false,
      overflow_capacity: true,          // Can accept overflow from engaged units
      staff_rested: true                // Medical staff well-rested
    },
    
    notes: 'Minimal battle casualties. Focus on DNBI, reconstitution, training, and CSR recovery.',
    romanian_term: 'Rezervă'
  }
};

// ============================================================================
// STATE TRANSITION RULES
// ============================================================================

export const STATE_TRANSITION_RULES = {
  COMBAT_TO_SUPPORT: {
    from: 'COMBAT_ENGAGED',
    to: 'SUPPORT_ACTIVE',
    triggers: [
      'Unit relieved by fresh unit',
      'Mission complete, consolidating',
      'Casualties exceed 30% - combat ineffective',
      'Ordered to support role'
    ],
    typical_duration_days: 1,
    casualty_spike: 1.1,
    notes: 'Usually coordinated with relief-in-place'
  },
  
  COMBAT_TO_RESERVE: {
    from: 'COMBAT_ENGAGED',
    to: 'TRUE_RESERVE',
    triggers: [
      'Extended combat period (>7 days)',
      'Unit requires reconstitution',
      'Strategic decision to preserve force',
      'Major equipment loss requiring refit',
      'CSR rates exceed threshold (>15%)'
    ],
    typical_duration_days: 2,
    casualty_spike: 1.15,
    notes: 'Requires movement to rear area, coordination with replacement unit',
    
    ukraine_lesson: 'Rotation after 5-7 days critical for maintaining combat effectiveness'
  },
  
  SUPPORT_TO_COMBAT: {
    from: 'SUPPORT_ACTIVE',
    to: 'COMBAT_ENGAGED',
    triggers: [
      'Committed to close combat',
      'FEBA moves past unit position',
      'Counterattack mission',
      'Emergency reinforcement',
      'Enemy breakthrough'
    ],
    typical_duration_days: 0.5,
    casualty_spike: 1.3,
    notes: 'Can happen quickly if bypassed or breakthrough occurs',
    
    ukraine_lesson: 'Artillery units committed to close combat in emergencies - very costly'
  },
  
  SUPPORT_TO_RESERVE: {
    from: 'SUPPORT_ACTIVE',
    to: 'TRUE_RESERVE',
    triggers: [
      'Support mission complete',
      'Unit rotation schedule',
      'Requires maintenance/refit'
    ],
    typical_duration_days: 1,
    casualty_spike: 1.0,
    notes: 'Normal operational cycle'
  },
  
  RESERVE_TO_SUPPORT: {
    from: 'TRUE_RESERVE',
    to: 'SUPPORT_ACTIVE',
    triggers: [
      'Rest period complete',
      'Unit committed to support operations',
      'Movement toward combat area'
    ],
    typical_duration_days: 1,
    casualty_spike: 1.0,
    notes: 'Typical return to operations'
  },
  
  RESERVE_TO_COMBAT: {
    from: 'TRUE_RESERVE',
    to: 'COMBAT_ENGAGED',
    triggers: [
      'Emergency commitment',
      'Counterattack/exploitation',
      'Relief of engaged unit',
      'Exploit success'
    ],
    typical_duration_days: 0.5,
    casualty_spike: 1.2,
    notes: 'Emergency only - prefer transition through SUPPORT',
    
    ukraine_lesson: 'Reserve commitment should be deliberate, not reactive'
  }
};

// ============================================================================
// UNIT TYPE TO DEFAULT STATE MAPPING
// ============================================================================

export const UNIT_TYPE_DEFAULT_STATE = {
  // Combat/Maneuver - Default to COMBAT_ENGAGED
  INFANTRY: 'COMBAT_ENGAGED',
  MECHANIZED: 'COMBAT_ENGAGED',
  ARMOR: 'COMBAT_ENGAGED',
  HEAVY: 'COMBAT_ENGAGED',
  AIRBORNE: 'COMBAT_ENGAGED',
  AIR_ASSAULT: 'COMBAT_ENGAGED',
  MOUNTAIN: 'COMBAT_ENGAGED',
  SPECIAL_FORCES: 'COMBAT_ENGAGED',
  RECONNAISSANCE: 'COMBAT_ENGAGED',
  
  // Combat Support - Default to SUPPORT_ACTIVE
  ARTILLERY: 'SUPPORT_ACTIVE',
  ENGINEERS: 'SUPPORT_ACTIVE',
  SIGNAL: 'SUPPORT_ACTIVE',
  AIR_DEFENSE: 'SUPPORT_ACTIVE',
  MILITARY_POLICE: 'SUPPORT_ACTIVE',
  CBRN_DEFENSE: 'SUPPORT_ACTIVE',   // 2026-10-24: was CHEMICAL (the US branch name); NATO and Romanian term: CBRN defence
  MILITARY_INTELLIGENCE: 'SUPPORT_ACTIVE',
  CIVIL_AFFAIRS: 'SUPPORT_ACTIVE',
  PSYCHOLOGICAL_OPS: 'SUPPORT_ACTIVE',
  
  // Combat Service Support - Default to TRUE_RESERVE
  LOGISTICS: 'TRUE_RESERVE',
  MEDICAL: 'TRUE_RESERVE',
  MAINTENANCE: 'TRUE_RESERVE',
  TRANSPORT: 'TRUE_RESERVE',
  
  // 2026-10-24 (naming audit step 4): the rows these types lacked (they fell to SUPPORT_ACTIVE without saying so)
  STRYKER: 'COMBAT_ENGAGED',          // a maneuver brigade like MECHANIZED
  SUPPLY: 'TRUE_RESERVE',
  MEDICAL_SUPPORT: 'TRUE_RESERVE',
  SUPPORT: 'TRUE_RESERVE',

  // Command - Position-dependent
  HQ: 'SUPPORT_ACTIVE',
  
  // Aviation
  AVIATION: 'SUPPORT_ACTIVE',
  ATTACK_AVIATION: 'SUPPORT_ACTIVE'
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get operational state data by value
 * @param {string} stateValue - COMBAT_ENGAGED, SUPPORT_ACTIVE, or TRUE_RESERVE
 * @returns {Object} State configuration object
 */
export function getOperationalState(stateValue) {
  return OPERATIONAL_STATES[stateValue] || OPERATIONAL_STATES.SUPPORT_ACTIVE;
}

/**
 * Get default operational state for a unit type
 * @param {string} unitType - Unit type from unitTypes.js
 * @returns {string} Default operational state value
 */
export function getDefaultStateForUnitType(unitType) {
  // Normalize unit type (handle variations)
  // 2026-10-24 (naming audit step 4): no unit type given is the only failsafe (SUPPORT_ACTIVE); a type that is not in the registry, or a
  // registry type without a row, throws instead of taking SUPPORT_ACTIVE silently.
  if (!unitType) return 'SUPPORT_ACTIVE';
  const normalized = unitType.toUpperCase().replace(/\s+/g, '_');
  return lookupByUnitType(UNIT_TYPE_DEFAULT_STATE, normalized, 'getDefaultStateForUnitType');
}

/**
 * Get battle casualty multiplier for a state
 * @param {string} stateValue - Operational state
 * @returns {number} Multiplier (1.0, 0.2, or 0.05)
 */
export function getBattleCasualtyMultiplier(stateValue) {
  const state = OPERATIONAL_STATES[stateValue];
  return state ? state.battle_casualty_multiplier : 0.2;
}

/**
 * Get DNBI rate for a state
 * @param {string} stateValue - Operational state
 * @param {boolean} useUkraineData - Use Ukraine-validated rates
 * @returns {number} DNBI rate per 1,000 per day
 */
export function getDNBIRate(stateValue, useUkraineData = true) {
  const state = OPERATIONAL_STATES[stateValue];
  if (!state) return 1.0;
  
  if (useUkraineData && state.ukraine_data?.dnbi_observed) {
    return state.ukraine_data.dnbi_observed;
  }
  
  return state.dnbi?.combined_rate || state.dnbi?.base_rate || 1.0;
}

/**
 * Get CSR rate for a state
 * @param {string} stateValue - Operational state
 * @param {boolean} useUkraineData - Use Ukraine-validated rates
 * @returns {number} CSR rate per 1,000 per day
 */
export function getCSRRate(stateValue, useUkraineData = true) {
  const state = OPERATIONAL_STATES[stateValue];
  if (!state) return 1.0;
  
  if (useUkraineData && state.ukraine_data?.csr_observed) {
    return state.ukraine_data.csr_observed;
  }
  
  return state.csr?.rate_per_1000_day || 1.0;
}

/**
 * Calculate cumulative CSR risk
 * @param {string} stateValue - Operational state
 * @param {number} daysInCombat - Days in continuous combat
 * @returns {number} Probability of CSR (0-1)
 */
export function calculateCSRRisk(stateValue, daysInCombat) {
  const state = OPERATIONAL_STATES[stateValue];
  if (!state || !state.csr) return 0;
  
  const { cumulative_risk_7_days, cumulative_risk_30_days } = state.csr;
  
  if (daysInCombat <= 0) return 0;
  if (daysInCombat <= 7) {
    return cumulative_risk_7_days * (daysInCombat / 7);
  }
  if (daysInCombat <= 30) {
    const additional = (cumulative_risk_30_days - cumulative_risk_7_days) * 
                       ((daysInCombat - 7) / 23);
    return cumulative_risk_7_days + additional;
  }
  
  // Beyond 30 days, risk continues to increase but at slower rate
  return Math.min(0.60, cumulative_risk_30_days + 0.01 * (daysInCombat - 30));
}

/**
 * Get RTD (Return to Duty) probability for CSR cases
 * @param {string} stateValue - Operational state
 * @returns {number} RTD probability (0-1)
 */
export function getCSRReturnToDutyRate(stateValue) {
  const state = OPERATIONAL_STATES[stateValue];
  return state?.csr?.rtd_probability || 0.70;
}

/**
 * Get all states as array for dropdowns
 * @returns {Array} Array of state options
 */
export function getOperationalStateOptions() {
  return Object.values(OPERATIONAL_STATES).map(state => ({
    value: state.value,
    label: state.label,
    shortLabel: state.shortLabel,
    icon: state.icon,
    color: state.color,
    multiplier: state.battle_casualty_multiplier,
    dnbi_rate: state.dnbi?.combined_rate
  }));
}

/**
 * Validate if a position is appropriate for a state
 * @param {string} stateValue - Operational state
 * @param {number} positionKm - Position from FEBA in km
 * @returns {Object} { valid: boolean, warning: string|null }
 */
export function validatePositionForState(stateValue, positionKm) {
  const state = OPERATIONAL_STATES[stateValue];
  if (!state) return { valid: true, warning: null };
  
  const { min, max } = state.position_range_km;
  
  if (positionKm < min) {
    return {
      valid: false,
      warning: `Position ${positionKm}km is too close to FEBA for ${state.label}. Expected ${min}-${max}km.`
    };
  }
  
  if (positionKm > max) {
    return {
      valid: false,
      warning: `Position ${positionKm}km is too far from FEBA for ${state.label}. Expected ${min}-${max}km.`
    };
  }
  
  return { valid: true, warning: null };
}

/**
 * Calculate combined casualty factor for a unit
 * @param {string} stateValue - Operational state
 * @param {Object} modifiers - Additional modifiers
 * @returns {number} Combined casualty factor
 */
export function calculateCombinedCasualtyFactor(stateValue, modifiers = {}) {
  let factor = getBattleCasualtyMultiplier(stateValue);
  
  // Apply additional modifiers if provided
  if (modifiers.posture) factor *= modifiers.posture;
  if (modifiers.threat) factor *= modifiers.threat;
  if (modifiers.terrain) factor *= modifiers.terrain;
  if (modifiers.deepStrike) {
    // Ukraine lesson: deep strikes increase support/reserve casualties
    const state = OPERATIONAL_STATES[stateValue];
    if (state?.ukraine_data?.deep_strike_factor) {
      factor *= state.ukraine_data.deep_strike_factor;
    }
  }
  
  return factor;
}

/**
 * Get rotation recommendation
 * @param {string} stateValue - Operational state
 * @param {number} daysEngaged - Days in current state
 * @returns {Object} Rotation recommendation
 */
export function getRotationRecommendation(stateValue, daysEngaged) {
  const state = OPERATIONAL_STATES[stateValue];
  if (!state || !state.planning) {
    return { status: 'OK', recommendation: null };
  }
  
  const { rest_requirement_days, max_continuous_days, recommended_rotation_days } = state.planning;
  
  if (!max_continuous_days) {
    return { status: 'OK', recommendation: null };
  }
  
  if (daysEngaged >= max_continuous_days) {
    return {
      status: 'CRITICAL',
      recommendation: `Unit has exceeded maximum continuous engagement (${max_continuous_days} days). IMMEDIATE rotation required to prevent combat ineffectiveness and excessive CSR.`
    };
  }
  
  if (recommended_rotation_days && daysEngaged >= recommended_rotation_days) {
    return {
      status: 'WARNING',
      recommendation: `Unit has reached recommended rotation threshold (${recommended_rotation_days} days). Plan rotation within ${max_continuous_days - daysEngaged} days.`
    };
  }
  
  if (rest_requirement_days && daysEngaged >= rest_requirement_days) {
    return {
      status: 'CAUTION',
      recommendation: `Unit approaching fatigue threshold. Consider rotation planning.`
    };
  }
  
  return { status: 'OK', recommendation: null };
}

/**
 * Calculate daily casualties for a unit in given state
 * @param {Object} config - Calculation configuration
 * @returns {Object} Daily casualty breakdown
 */
export function calculateDailyCasualties(config) {
  const {
    stateValue,
    strength,
    baseDailyCasualtyRate = 0.02,
    useUkraineData = true
  } = config;
  
  const state = OPERATIONAL_STATES[stateValue];
  if (!state) {
    return { battle: 0, dnbi: 0, csr: 0, total: 0 };
  }
  
  // Battle casualties
  const battleMultiplier = state.battle_casualty_multiplier;
  const battleCasualties = Math.round(strength * baseDailyCasualtyRate * battleMultiplier);
  
  // DNBI
  const dnbiRate = getDNBIRate(stateValue, useUkraineData) / 1000;
  const dnbiCasualties = Math.round(strength * dnbiRate);
  
  // CSR
  const csrRate = getCSRRate(stateValue, useUkraineData) / 1000;
  const csrCasualties = Math.round(strength * csrRate);
  
  return {
    battle: battleCasualties,
    dnbi: dnbiCasualties,
    csr: csrCasualties,
    total: battleCasualties + dnbiCasualties + csrCasualties,
    rates: {
      battle: battleMultiplier,
      dnbi: dnbiRate * 1000,
      csr: csrRate * 1000
    }
  };
}

// ============================================================================
// STATE ICONS AND COLORS
// ============================================================================

export const STATE_ICONS = {
  COMBAT_ENGAGED: TACTICAL_ICONS.ATTACK,
  SUPPORT_ACTIVE: ICONS.GENERAL.GEAR,
  TRUE_RESERVE: TACTICAL_ICONS.RESERVE
};

export const STATE_COLORS = {
  COMBAT_ENGAGED: { 
    primary: '#f44336', 
    light: '#ffebee', 
    text: '#b71c1c',
    mui: 'error'
  },
  SUPPORT_ACTIVE: { 
    primary: '#ff9800', 
    light: '#fff3e0', 
    text: '#e65100',
    mui: 'warning'
  },
  TRUE_RESERVE: { 
    primary: '#9e9e9e', 
    light: '#fafafa', 
    text: '#424242',
    mui: 'default'
  }
};

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const OPERATIONAL_STATES_EDUCATIONAL = {
  key_principles: [
    'Operational state is PRIMARY determinant of casualty rate',
    'COMBAT_ENGAGED: 100% of calculated battle casualties',
    'SUPPORT_ACTIVE: 20% of calculated battle casualties',
    'TRUE_RESERVE: 5% of calculated battle casualties (DNBI only significant)',
    'State also affects DNBI and CSR rates'
  ],
  
  ukraine_lessons: [
    'Deep strikes increase SUPPORT casualties by 25% above historical',
    'Reserve areas not completely safe (Makiivka attack)',
    'CSR rates higher than NATO estimates due to drone threat',
    'Rotation after 5-7 days critical for combat effectiveness',
    'Trench warfare conditions dramatically increase DNBI'
  ],
  
  medical_planning: [
    'COMBAT: Full medical support, golden hour critical',
    'SUPPORT: Priority evacuation, moderate medical presence',
    'RESERVE: Routine care, can provide overflow capacity',
    'CSR recovery primarily occurs in RESERVE state'
  ],
  
  common_errors: [
    'Assuming RESERVE has zero casualties (5% battle + DNBI still occur)',
    'Not accounting for deep strike effect on SUPPORT units',
    'Ignoring CSR accumulation over extended engagement',
    'Not planning rotation cycles'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
// ============================================================================
// SPM-02: STRENGTH EFFECTIVENESS THRESHOLDS
// Migrated from strengthProjectionModule.js (February 24, 2026).
// Source: NATO AJP-3.3; US FM 3-96 Brigade Combat Team; ATP 4-02.
// Defines combat effectiveness bands by personnel strength percentage.
// ============================================================================

export const STRENGTH_EFFECTIVENESS = {
  FULL_EFFECTIVE: {
    percent: 100,
    min: 85,
    status: 'FULL',
    color: 'success',
    description: 'Full combat effective - all missions capable',
    icon: 'CheckCircle'
  },
  REDUCED_CAPABILITY: {
    percent: 85,
    min: 70,
    status: 'REDUCED',
    color: 'warning',
    description: 'Reduced capability - cannot conduct main attack',
    icon: 'Warning'
  },
  MISSION_DEGRADED: {
    percent: 70,
    min: 50,
    status: 'DEGRADED',
    color: 'error',
    description: 'Mission degraded - defensive operations only',
    icon: 'Error'
  },
  COMBAT_INEFFECTIVE: {
    percent: 50,
    min: 30,
    status: 'INEFFECTIVE',
    color: 'error',
    description: 'Combat ineffective - reconstitution required',
    icon: 'Cancel'
  },
  DESTROYED: {
    percent: 30,
    min: 0,
    status: 'DESTROYED',
    color: 'error',
    description: 'Unit destroyed - merge with another unit',
    icon: 'Block'
  }
};

// ============================================================================
// IMP-008: STRENGTH COHESION MODIFIERS (March 10, 2026)
// Per-capita casualty rate multiplier applied when unit crosses strength bands.
// Source: US FM 101-5-1 Appendix C (Gumball Chart); Dupuy TNDM cohesion research.
// Rationale: As unit strength falls, remaining soldiers lose mutual fire support,
// cover discipline degrades, and medical carry capacity is overwhelmed — producing
// disproportionately higher per-capita casualties vs. the linear PAR model.
// These are NOT mandatory withdrawal thresholds (commanders may choose to fight on)
// but they DO describe what statistically happens to per-capita loss rates.
// NATO/AJMedP-1 uses a linear PAR model; these modifiers are a Dupuy supplement
// for realistic medical planning in sustained high-intensity operations.
// ============================================================================

export const STRENGTH_COHESION_MODIFIERS = {
  FULL_EFFECTIVE: {
    label: 'Full Effective',
    min_percent: 85,
    max_percent: 100,
    modifier: 1.00,
    color: 'success',
    fm_color: 'GREEN',
    description: 'No cohesion penalty — full mutual support and command integrity'
  },
  REDUCED_CAPABILITY: {
    label: 'Reduced Capability',
    min_percent: 70,
    max_percent: 84,
    modifier: 1.08,
    color: 'warning',
    fm_color: 'AMBER',
    description: 'Minor cohesion degradation — small gaps in line, some command continuity loss'
  },
  MISSION_DEGRADED: {
    label: 'Mission Degraded',
    min_percent: 50,
    max_percent: 69,
    modifier: 1.20,
    color: 'error',
    fm_color: 'RED',
    description: 'Significant cohesion loss — reduced mutual support, cover discipline degraded'
  },
  COMBAT_INEFFECTIVE: {
    label: 'Combat Ineffective',
    min_percent: 30,
    max_percent: 49,
    modifier: 1.40,
    color: 'error',
    fm_color: 'BLACK',
    description: 'Cohesion collapse — surviving elements exposed, medical carry capacity overwhelmed'
  }
};

/**
 * IMP-008: Returns the per-capita casualty rate multiplier for the given strength percentage.
 * @param {number} strengthPercent - Current strength as % of initial (0–100)
 * @returns {number} Modifier (1.00, 1.08, 1.20, or 1.40)
 */
export function getCohesionModifier(strengthPercent) {
  if (strengthPercent >= 85) return STRENGTH_COHESION_MODIFIERS.FULL_EFFECTIVE.modifier;
  if (strengthPercent >= 70) return STRENGTH_COHESION_MODIFIERS.REDUCED_CAPABILITY.modifier;
  if (strengthPercent >= 50) return STRENGTH_COHESION_MODIFIERS.MISSION_DEGRADED.modifier;
  if (strengthPercent >= 30) return STRENGTH_COHESION_MODIFIERS.COMBAT_INEFFECTIVE.modifier;
  return 1.0; // <30% — handled by `continue` exit in day loop; modifier irrelevant
}

/**
 * IMP-008: Returns the band label for the given strength percentage.
 * Used by Tab 5 day-by-day table to render the FM 101-5-1 color chip.
 * @param {number} strengthPercent - Current strength as % of initial (0–100)
 * @returns {string} Band key (FULL_EFFECTIVE | REDUCED_CAPABILITY | MISSION_DEGRADED | COMBAT_INEFFECTIVE | DESTROYED)
 */
export function getCohesionBand(strengthPercent) {
  if (strengthPercent >= 85) return 'FULL_EFFECTIVE';
  if (strengthPercent >= 70) return 'REDUCED_CAPABILITY';
  if (strengthPercent >= 50) return 'MISSION_DEGRADED';
  if (strengthPercent >= 30) return 'COMBAT_INEFFECTIVE';
  return 'DESTROYED'; // <30% — unit exits day loop via `continue`
}

// ============================================================================
// SPM-06: PERSONNEL CATEGORIES
// Migrated from strengthProjectionModule.js (February 24, 2026).
// Used for replacement tracking and casualty reporting by category.
// QM-6I-001 fix: STANAG 2958 not confirmed in any public NATO registry.
// Correct reference: STANAG 2542 (AJMedP-1) — Allied Joint Medical Planning Doctrine,
// which defines casualty categories (KIA/DOW/WIA/DNBI/RTD) and replacement planning methodology.
// US force management: US ARFORGEN framework (FM 1-01).
// ============================================================================

export const PERSONNEL_CATEGORIES = {
  COMBAT: {
    label: 'Combat Personnel',
    includes: ['Infantry', 'Armor', 'Artillery', 'Engineer'],
    priority_modifier: 1.0
  },
  MEDICAL: {
    label: 'Medical Personnel',
    includes: ['Physician', 'PA', 'Nurse', 'Medic', '68W'],
    priority_modifier: 1.2  // Higher replacement priority for medical
  },
  SUPPORT: {
    label: 'Support Personnel',
    includes: ['Logistics', 'Communications', 'Maintenance'],
    priority_modifier: 0.8
  }
};


const operationalStatesConstants = {
  OPERATIONAL_STATES,
  STATE_TRANSITION_RULES,
  UNIT_TYPE_DEFAULT_STATE,
  STRENGTH_EFFECTIVENESS,     // SPM-02
  STRENGTH_COHESION_MODIFIERS, // IMP-008
  PERSONNEL_CATEGORIES,       // SPM-06
  STATE_ICONS,
  STATE_COLORS,
  getOperationalState,
  getDefaultStateForUnitType,
  getBattleCasualtyMultiplier,
  getDNBIRate,
  getCSRRate,
  calculateCSRRisk,
  getCSRReturnToDutyRate,
  getOperationalStateOptions,
  validatePositionForState,
  calculateCombinedCasualtyFactor,
  getRotationRecommendation,
  calculateDailyCasualties,
  getCohesionModifier,        // IMP-008
  getCohesionBand,            // IMP-008
  OPERATIONAL_STATES_EDUCATIONAL
};

export default operationalStatesConstants;
